import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { ExportTraceLookup } from "@/components/ExportTraceLookup";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { GeoCell } from "@/components/GeoCell";

export const SecurityFlagsPanel = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/security/flagged-ips`, { headers: getAuthHeader() });
      setItems(r.data.items);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load flagged IPs"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verify = async (it) => {
    try {
      await axios.post(`${API}/admin/users/${it.user_id}/ips/verify`, { ip: it.ip }, { headers: getAuthHeader() });
      toast.success(`${it.ip} verified for ${it.name || it.email}`); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };
  const disable = async (it) => {
    if (!window.confirm(`Disable ${it.name || it.email} and log them out everywhere?`)) return;
    try {
      await axios.post(`${API}/admin/users/${it.user_id}/disable`, { reason: `Suspicious IP ${it.ip}` }, { headers: getAuthHeader() });
      toast.success("User disabled"); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="security-flags-panel">
      <ExportTraceLookup />
      <p className="text-sm text-slate-600 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600" />
        Logins from a NEW IP are allowed but flagged here. Red = NEW COUNTRY, orange = new city for that user (compared with their verified IPs). Verify the IP if it is expected, or disable the user if it looks like misuse.
      </p>
      <div className="border rounded-lg overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-amber-50 text-amber-900">
            <tr>{["User", "Team", "New IP", "Location", "First seen", ""].map(h => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(items || []).map((it, i) => (
              <tr key={`${it.user_id}-${it.ip}`} className={`border-t ${it.new_country ? "bg-red-50" : it.new_city ? "bg-orange-50" : ""}`} data-testid={`flagged-ip-${i}`}>
                <td className="p-2 font-medium">{it.name}<div className="text-[10px] text-slate-400">{it.email}</div></td>
                <td className="p-2">{it.team}</td>
                <td className="p-2 font-mono text-amber-800 font-semibold">{it.ip}</td>
                <td className="p-2">
                  <GeoCell geo={it.geo} ip={it.ip} testId={`flagged-ip-geo-${i}`} />
                  {it.new_country && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold" data-testid={`flagged-ip-new-country-${i}`}>NEW COUNTRY</span>}
                  {it.new_city && <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-500 text-white text-[10px] font-bold" data-testid={`flagged-ip-new-city-${i}`}>NEW CITY</span>}
                </td>
                <td className="p-2">{it.first_seen ? new Date(it.first_seen).toLocaleString() : "—"}</td>
                <td className="p-2 flex gap-1">
                  <Button size="sm" className="h-6 px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => verify(it)} data-testid={`flagged-ip-verify-${i}`}>
                    <ShieldCheck className="w-3 h-3 mr-1" /> Verify
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-red-600 border-red-200" onClick={() => disable(it)} data-testid={`flagged-ip-disable-${i}`}>
                    Disable user
                  </Button>
                </td>
              </tr>
            ))}
            {items && !items.length && <tr><td colSpan={6} className="p-6 text-center text-slate-400" data-testid="flagged-ip-empty">No unverified IPs — all clear</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
